/**
 * Модалка добавления synthetic-колонки `reference_attribute` —
 * вывод значения колонки справочника (или цепочки колонок справочников)
 * по reference-полю текущей записи.
 *
 * Принцип работы:
 *   1. Пользователь выбирает reference-поле текущего item type.
 *   2. По нему резолвится связанный dictionaryId, грузятся колонки.
 *   3. Пользователь выбирает колонку. Если колонка сама типа `reference`,
 *      появляется следующий шаг: колонки её целевого справочника, и т.д.
 *   4. Любая non-reference колонка (или достижение лимита глубины) завершает путь.
 *   5. Резолв полностью клиентский: см. refAttrPath.ts.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Breadcrumb, Form, Input, Modal, Select, Spin } from 'antd';
import type {
  DictionaryColumn,
  FieldDefinition,
  ReferenceAttributeColumnConfig,
} from '@lens/shared';
import { REFERENCE_ATTRIBUTE_MAX_DEPTH } from '@lens/shared';
import { getDictionary } from '../dictionaries/api.ts';

export interface ReferenceAttributeColumnModalProps {
  open: boolean;
  /** Поля текущего view (с учётом merge для multi-type). Берём только reference-поля. */
  fields: FieldDefinition[];
  /** Если задан — модалка работает в режиме редактирования: восстанавливает цепочку из конфига. */
  initialConfig?: ReferenceAttributeColumnConfig | null;
  onClose: () => void;
  onSubmit: (cfg: Omit<ReferenceAttributeColumnConfig, 'key'>) => Promise<void> | void;
}

/** Один уровень цепочки: справочник + загруженные колонки + выбранная колонка. */
interface PathLevel {
  dictionaryId: string;
  dictionaryName: string;
  columns: DictionaryColumn[];
  /** slug выбранной колонки на этом уровне (или null, если ещё не выбрана). */
  selected: string | null;
}

export function ReferenceAttributeColumnModal({
  open,
  fields,
  initialConfig,
  onClose,
  onSubmit,
}: ReferenceAttributeColumnModalProps): JSX.Element {
  const isEditing = Boolean(initialConfig);
  const [referenceFieldSlug, setReferenceFieldSlug] = useState<string | null>(null);
  const [label, setLabel] = useState('');
  const [labelEdited, setLabelEdited] = useState(false);
  const [levels, setLevels] = useState<PathLevel[]>([]);
  const [loadingDict, setLoadingDict] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Пропускаем «корневую» загрузку справочника один раз — когда цепочку
  // восстанавливает гидратация из initialConfig (иначе она затрёт уровни).
  const suppressRootLoadRef = useRef(false);
  // key конфига, который уже гидратирован за текущее открытие модалки.
  const hydratedKeyRef = useRef<string | null>(null);

  const referenceFields = useMemo(
    () => fields.filter((f) => f.fieldType === 'reference'),
    [fields],
  );

  // Reset на закрытии.
  useEffect(() => {
    if (!open) {
      setReferenceFieldSlug(null);
      setLabel('');
      setLabelEdited(false);
      setLevels([]);
      setError(null);
      suppressRootLoadRef.current = false;
      hydratedKeyRef.current = null;
    }
  }, [open]);

  // Режим редактирования: при открытии восстанавливаем поле, заголовок и
  // всю цепочку справочников из сохранённого конфига.
  useEffect(() => {
    if (!open || !initialConfig) return;
    if (hydratedKeyRef.current === initialConfig.key) return;
    hydratedKeyRef.current = initialConfig.key;
    setLabel(initialConfig.label);
    setLabelEdited(true);

    const field = referenceFields.find((f) => f.slug === initialConfig.referenceFieldSlug);
    const rootDictId = (field?.config as { dictionaryId?: string } | null)?.dictionaryId;
    suppressRootLoadRef.current = true;
    setReferenceFieldSlug(initialConfig.referenceFieldSlug);
    if (!rootDictId) return;

    let cancelled = false;
    setLoadingDict(true);
    setError(null);
    (async () => {
      try {
        const rebuilt: PathLevel[] = [];
        let dictId: string | undefined = rootDictId;
        for (let i = 0; i < initialConfig.path.length && dictId; i += 1) {
          const dict = await getDictionary(dictId);
          if (cancelled) return;
          const columns = (dict.columns ?? []) as DictionaryColumn[];
          const selectedSlug = initialConfig.path[i] ?? null;
          rebuilt.push({ dictionaryId: dictId, dictionaryName: dict.name, columns, selected: selectedSlug });
          const col = columns.find((c) => c.slug === selectedSlug);
          if (col?.columnType === 'reference' && i + 1 < initialConfig.path.length) {
            dictId = (col.config as { dictionaryId?: string } | null)?.dictionaryId;
          } else {
            break;
          }
        }
        if (!cancelled) setLevels(rebuilt);
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Не удалось восстановить цепочку');
        }
      } finally {
        if (!cancelled) setLoadingDict(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, initialConfig, referenceFields]);

  // При смене reference-поля — грузим корневой справочник.
  useEffect(() => {
    if (!open || !referenceFieldSlug) {
      setLevels([]);
      return;
    }
    // Если поле выставила гидратация из initialConfig — не сбрасываем цепочку.
    if (suppressRootLoadRef.current) {
      suppressRootLoadRef.current = false;
      return;
    }
    const field = referenceFields.find((f) => f.slug === referenceFieldSlug);
    const dictionaryId = (field?.config as { dictionaryId?: string } | null)?.dictionaryId;
    if (!dictionaryId) {
      setLevels([]);
      return;
    }
    let cancelled = false;
    setLoadingDict(true);
    setError(null);
    getDictionary(dictionaryId)
      .then((dict) => {
        if (cancelled) return;
        setLevels([{
          dictionaryId,
          dictionaryName: dict.name,
          columns: (dict.columns ?? []) as DictionaryColumn[],
          selected: null,
        }]);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Не удалось загрузить справочник');
      })
      .finally(() => {
        if (!cancelled) setLoadingDict(false);
      });
    return () => { cancelled = true; };
  }, [open, referenceFieldSlug, referenceFields]);

  const handleSelectAtLevel = async (levelIndex: number, columnSlug: string) => {
    const level = levels[levelIndex];
    if (!level) return;
    const col = level.columns.find((c) => c.slug === columnSlug);
    if (!col) return;

    // Обрезаем уровни справа от текущего и фиксируем выбор.
    const head = levels.slice(0, levelIndex);
    const updated: PathLevel = { ...level, selected: columnSlug };
    setLevels([...head, updated]);

    // Авто-подстановка label по последнему выбору, пока пользователь не правил вручную.
    if (!labelEdited && col.name) {
      setLabel(col.name);
    }

    // Если колонка — reference и не достигнут лимит глубины, грузим следующий справочник.
    const newDepth = levelIndex + 1; // сколько уже выбрано после этого клика
    if (col.columnType === 'reference' && newDepth < REFERENCE_ATTRIBUTE_MAX_DEPTH) {
      const nextDictId = (col.config as { dictionaryId?: string } | null)?.dictionaryId;
      if (nextDictId) {
        setLoadingDict(true);
        setError(null);
        try {
          const dict = await getDictionary(nextDictId);
          setLevels((prev) => {
            // На случай, если пользователь успел перевыбрать что-то выше за время загрузки.
            if (prev.length !== newDepth) return prev;
            return [
              ...prev,
              {
                dictionaryId: nextDictId,
                dictionaryName: dict.name,
                columns: (dict.columns ?? []) as DictionaryColumn[],
                selected: null,
              },
            ];
          });
        } catch (err: unknown) {
          setError(err instanceof Error ? err.message : 'Не удалось загрузить справочник');
        } finally {
          setLoadingDict(false);
        }
      }
    }
  };

  // Путь считается завершённым, как только выбрана хотя бы одна колонка.
  // Reference-колонка — допустимый лист: она резолвится в отображаемое значение
  // связанной записи (см. formatLeafValue в refAttrPath.ts). Углубление дальше
  // по цепочке reference-колонок — необязательно.
  const path = useMemo(
    () => levels.map((l) => l.selected).filter((s): s is string => Boolean(s)),
    [levels],
  );
  const isPathComplete = path.length > 0;

  const handleOk = async () => {
    if (!referenceFieldSlug || !isPathComplete || !label.trim()) {
      setError('Заполните все поля и доведите цепочку до листа');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        kind: 'reference_attribute',
        label: label.trim(),
        referenceFieldSlug,
        path,
        attributeSlug: path[0],
      });
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Не удалось сохранить колонку');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Колонка: атрибут справочника"
      onCancel={onClose}
      onOk={() => void handleOk()}
      okText={isEditing ? 'Сохранить' : 'Добавить'}
      cancelText="Отмена"
      confirmLoading={submitting}
      destroyOnClose
      width={640}
    >
      {error && <Alert type="error" message={error} style={{ marginBottom: 12 }} />}
      <Form layout="vertical">
        <Form.Item label="Reference-поле" required>
          <Select
            value={referenceFieldSlug}
            onChange={(v) => {
              setReferenceFieldSlug(v);
              setLevels([]);
            }}
            placeholder="Выберите reference-поле"
            options={referenceFields.map((f) => ({ value: f.slug, label: f.name }))}
            showSearch
            optionFilterProp="label"
          />
        </Form.Item>

        {levels.length > 0 && (
          <Form.Item label="Цепочка атрибутов" required>
            {/* Хлебные крошки: справочник → выбранная колонка → справочник → ... */}
            <Breadcrumb
              style={{ marginBottom: 8 }}
              items={(() => {
                const crumbs: Array<{ title: string }> = [];
                for (let i = 0; i < levels.length; i += 1) {
                  const lvl = levels[i];
                  if (!lvl) continue;
                  crumbs.push({ title: lvl.dictionaryName });
                  if (lvl.selected) {
                    const col = lvl.columns.find((c) => c.slug === lvl.selected);
                    crumbs.push({ title: col?.name ?? lvl.selected });
                  }
                }
                return crumbs;
              })()}
            />
            {/* Один Select на каждый уровень. */}
            {levels.map((level, idx) => (
              <div key={`${level.dictionaryId}-${idx}`} style={{ marginBottom: 8 }}>
                <Select
                  value={level.selected}
                  onChange={(v) => void handleSelectAtLevel(idx, v)}
                  placeholder={`Колонка из «${level.dictionaryName}»`}
                  style={{ width: '100%' }}
                  options={level.columns.map((c) => ({
                    value: c.slug,
                    label: c.columnType === 'reference' ? `${c.name} →` : c.name,
                  }))}
                  showSearch
                  optionFilterProp="label"
                />
              </div>
            ))}
            {loadingDict && <Spin size="small" />}
            {levels.length >= REFERENCE_ATTRIBUTE_MAX_DEPTH && (
              <Alert
                type="info"
                showIcon
                style={{ marginTop: 8 }}
                message={`Достигнут лимит глубины (${REFERENCE_ATTRIBUTE_MAX_DEPTH} уровней).`}
              />
            )}
          </Form.Item>
        )}

        <Form.Item label="Заголовок колонки" required>
          <Input
            value={label}
            onChange={(e) => { setLabel(e.target.value); setLabelEdited(true); }}
            placeholder="Как назвать колонку"
            maxLength={200}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export function extractReferenceAttributeColumns(config: unknown): ReferenceAttributeColumnConfig[] {
  const cfg = config as { columns?: unknown } | null;
  if (!cfg || !Array.isArray(cfg.columns)) return [];
  return cfg.columns
    .filter(
      (column): column is Record<string, unknown> =>
        !!column &&
        typeof column === 'object' &&
        (column as { kind?: unknown }).kind === 'reference_attribute',
    )
    .map((column) => {
      // Backward-compat: если в сохранённом конфиге нет path, поднимем из attributeSlug.
      const obj = column;
      if (!Array.isArray(obj.path) && typeof obj.attributeSlug === 'string') {
        obj.path = [obj.attributeSlug];
      }
      return obj as unknown as ReferenceAttributeColumnConfig;
    });
}

export function referenceAttributeColumnSlug(key: string): string {
  return `_refattr:${key}`;
}
